const CLIENT_STATES = ['affiliated', 'contacted', 'visited', 'completed'];

export function parseRecordDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function startOfDay(date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

export function endOfDay(date) {
  const next = new Date(date);
  next.setHours(23, 59, 59, 999);
  return next;
}

export function isWithinDateRange(recordDate, dateFrom, dateTo) {
  if (!recordDate) return !dateFrom && !dateTo;
  const value = startOfDay(recordDate).getTime();
  if (dateFrom && value < startOfDay(dateFrom).getTime()) return false;
  if (dateTo && value > endOfDay(dateTo).getTime()) return false;
  return true;
}

export function clientMatchesVendor(client, vendorId, vendors = []) {
  if (!vendorId) return true;
  const vendor = vendors.find((item) => String(item.id) === String(vendorId));
  if (!vendor) return true;
  if (Array.isArray(client.vendor_ids) && client.vendor_ids.some((id) => String(id) === String(vendorId))) {
    return true;
  }
  if (Array.isArray(client.vendor_usernames) && client.vendor_usernames.includes(vendor.username)) {
    return true;
  }
  return false;
}

export function prospectMatchesVendor(prospect, vendorId) {
  if (!vendorId) return true;
  return String(prospect.vendor_id) === String(vendorId);
}

export function filterClients(clients = [], { dateFrom = null, dateTo = null, vendorId = '' } = {}, vendors = []) {
  return clients.filter((client) => {
    const recordDate = parseRecordDate(client.created_at || client.state_updated_at);
    if (!isWithinDateRange(recordDate, dateFrom, dateTo)) return false;
    return clientMatchesVendor(client, vendorId, vendors);
  });
}

export function filterProspects(prospects = [], { dateFrom = null, dateTo = null, vendorId = '' } = {}) {
  return prospects.filter((prospect) => {
    const recordDate = parseRecordDate(prospect.created_at || prospect.updated_at);
    if (!isWithinDateRange(recordDate, dateFrom, dateTo)) return false;
    return prospectMatchesVendor(prospect, vendorId);
  });
}

export function buildClientStateCounts(clients = []) {
  const counts = CLIENT_STATES.reduce((acc, state) => {
    acc[state] = 0;
    return acc;
  }, {});

  clients.forEach((client) => {
    const state = client.state;
    if (state && counts[state] !== undefined) {
      counts[state] += 1;
    }
  });

  return counts;
}

function incrementHistoricalCount(counts, state, recordDate, dateFrom, dateTo) {
  if (!state || counts[state] === undefined) return;
  if (!isWithinDateRange(recordDate, dateFrom, dateTo)) return;
  counts[state] += 1;
}

export function buildClientHistoricalStateCounts(
  clients = [],
  { dateFrom = null, dateTo = null, vendorId = '' } = {},
  vendors = [],
) {
  const counts = CLIENT_STATES.reduce((acc, state) => {
    acc[state] = 0;
    return acc;
  }, {});

  clients
    .filter((client) => clientMatchesVendor(client, vendorId, vendors))
    .forEach((client) => {
      const history = Array.isArray(client.state_history) ? client.state_history : [];
      if (history.length === 0) {
        incrementHistoricalCount(
          counts,
          client.state,
          parseRecordDate(client.state_updated_at || client.created_at),
          dateFrom,
          dateTo,
        );
        return;
      }

      history.forEach((entry) => {
        incrementHistoricalCount(
          counts,
          entry.to_state,
          parseRecordDate(entry.created_at),
          dateFrom,
          dateTo,
        );
      });
    });

  return counts;
}

export function buildProspectHistoricalStateCounts(
  prospects = [],
  { dateFrom = null, dateTo = null, vendorId = '' } = {},
) {
  const counts = {};

  prospects
    .filter((prospect) => prospectMatchesVendor(prospect, vendorId))
    .forEach((prospect) => {
      const history = Array.isArray(prospect.state_history) ? prospect.state_history : [];
      if (history.length === 0) {
        const code = String(prospect?.status || '').trim().toUpperCase();
        if (!code) return;
        const recordDate = parseRecordDate(prospect.updated_at || prospect.created_at);
        if (!isWithinDateRange(recordDate, dateFrom, dateTo)) return;
        counts[code] = (counts[code] || 0) + 1;
        return;
      }

      history.forEach((entry) => {
        const code = String(entry?.to_state || '').trim().toUpperCase();
        if (!code) return;
        const recordDate = parseRecordDate(entry.created_at);
        if (!isWithinDateRange(recordDate, dateFrom, dateTo)) return;
        counts[code] = (counts[code] || 0) + 1;
      });
    });

  return counts;
}

export function buildDistributionRows(stateData = {}, labels = {}) {
  return Object.entries(stateData).map(([key, count]) => {
    const safeCount = count || 0;
    return {
      key,
      label: labels[key] || key,
      count: safeCount,
    };
  });
}

export function withPercentages(rows = []) {
  const total = rows.reduce((sum, row) => sum + (row.count || 0), 0);
  return rows.map((row) => ({
    ...row,
    percentage: total > 0 ? ((row.count / total) * 100).toFixed(1) : '0.0',
    total,
  }));
}
